import React from "react";
import { NavigationProp, ParamListBase, useIsFocused } from "@react-navigation/native";
import { upperFirst } from "lodash";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";

import { tAdminConnectionResponse } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import * as utilsDb from "src/db/utils.db";
import * as connectionDb from "src/db/connection.db";

import { kColors } from "src/kColors";
import { safeJoin } from "src/util/gFormatting";
import { kStyleConst } from "src/kStyle";
import { useAppDispatch } from "src/redux";
import { PdCopy, PdSettings } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import {
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/v2/layout/table";
import { PdSearchInput } from "src/components/v2/ui";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useDebounce, useIsMobile } from "src/hooks/ui";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import { tPressableState } from "src/components/types";
import { kRoute } from "src/kRoute";
import { kTablePageSize } from "src/util/constants";
import { changeFilterNextPage, changeFilterPrevPage, changeFilterTerm, resetFilters } from "src/redux/filters/actions";
import { PdFormLabel } from "src/components/v2/forms/control";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";

type tTableState = {
  data: tAdminConnectionResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const formatDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const P14_ViewCustomers: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const dispatch = useAppDispatch();
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.customers);
  const filters = useSelector((state) => state.FiltersReducer.customers);
  const [tableState, setTableState] = React.useState<tTableState>({
    data: [],
    isLoading: true,
    prev: null,
    next: null,
  });
  const isFocused = useIsFocused();
  const dTermFilter = useDebounce(filters.term, 500);
  const tableInstance = useTable<tAdminConnectionResponse>({
    data: tableState.data,
    columns,
    initialState: {
      sortedBy: "connectionDate",
      sortDirection: "desc",
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });
  const isMobile = useIsMobile();

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const updateHiddenColumns = (newTableConfig: Record<keyof tAdminConnectionResponse, boolean>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  const handleOnRowPress = (item: tRowInstance<tAdminConnectionResponse>) => {
    if (item.entityType === "brand") {
      navigation.navigate(kRoute.P06_09, { brandId: item.id });
    } else if (item.entityType === "retailer") {
      navigation.navigate(kRoute.P06_10, { retailerId: item.id });
    } else if (item.entityType === "rep") {
      navigation.navigate(kRoute.P06_11, { repId: item.id });
    }
  };

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "customers", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "customers", value: tableState?.next ?? "" }));
  };

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const params: connectionDb.tQueryAdminConnectionsParams = { term: dTermFilter, status: "Approved" };
      const query = await utilsDb.withPagination(
        connectionDb.queryAdminConnection,
        connectionDb.documentAdminConnection,
        {
          ...params,
          prev: filters.prev,
          next: filters.next,
          limit: kTablePageSize,
        },
      );

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapAdminConnection(doc.data()));
        setTableState({
          data,
          isLoading: false,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [dTermFilter, filters.next, filters.prev]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  if (tableState.isLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdText variant="subheader" style={styles.mb4}>
        Customers
      </PdText>
      <View style={styles.mb4}>
        {!isMobile && <PdFormLabel>Search your customers</PdFormLabel>}
        <PdSearchInput
          value={filters.term}
          onChangeText={(value) => dispatch(changeFilterTerm({ pageName: "customers", value }))}
        />
      </View>
      {!isMobile && (
        <PdStack style={styles.tableOptionsContainer}>
          <PdButton
            colorScheme="gray"
            size="sm"
            rightIcon={<PdCopy style={styles.iconSm} />}
            onPress={handleOnPressCopyAsCSV}
          >
            <PdButtonLabel>Copy as CSV</PdButtonLabel>
          </PdButton>
          <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
            <PdPopoverTrigger>
              <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                <PdButtonLabel>Configure Table</PdButtonLabel>
              </PdButton>
            </PdPopoverTrigger>
            <PdPopoverContent style={styles.configureTablePopoverContent}>
              <PopoverContentTableColumns
                columns={columns}
                tableConfigReduxKey="customers"
                onPressCheckbox={updateHiddenColumns}
              />
            </PdPopoverContent>
          </PdPopover>
        </PdStack>
      )}
      <PdTable
        ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
        ListEmptyComponent={PdTableEmpty}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <Pressable onPress={() => handleOnRowPress(item)}>
            {({ pressed, hovered }: tPressableState) => (
              <PdTableRow style={[hovered ? styles.tableRowHovered : {}, pressed ? styles.tableRowPressed : {}]}>
                {item.cells.map((cell) => (
                  <PdTableCell key={cell.uniqueId} style={cell.style}>
                    {cell.render("Cell")}
                  </PdTableCell>
                ))}
              </PdTableRow>
            )}
          </Pressable>
        )}
      />
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </View>
  );
};

export const P14 = React.memo(P14_ViewCustomers);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  flex1: {
    flex: 1,
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobilePage: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 2,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  page: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingTop: kStyleConst.kSpacing * 8,
  },
  tableColumnLg: {
    width: 300,
  },
  tableColumnMd: {
    width: 160,
  },
  tableColumnSm: {
    width: 100,
  },
  tableOptionsContainer: {
    marginBottom: kStyleConst.kSpacing * 2,
    marginLeft: "auto",
    zIndex: 1,
  },
  tableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  tableRowPressed: {
    opacity: 0.6,
  },
});

const columns: tColumn<tAdminConnectionResponse>[] = [
  {
    id: "entityType",
    header: "Type",
    accessor: (originalRow) => upperFirst(originalRow.entityType),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "retailerAccountManagerName",
    header: "Account Manager",
    accessor: (originalRow) => originalRow.applicantAccountManagerName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "profile",
    header: "Name",
    accessor: (originalRow) => originalRow.profile,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "applicantEmail",
    header: "Email",
    accessor: (originalRow) => originalRow.applicantEmail,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "applicantWebsite",
    header: "Website",
    accessor: (originalRow) => originalRow.applicantWebsite,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "applicantInstagram",
    header: "Instagram",
    accessor: (originalRow) => originalRow.applicantInstagram || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "applicantFacebook",
    header: "Facebook",
    accessor: (originalRow) => originalRow.applicantFacebook || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "applicantPhone",
    header: "Phone",
    accessor: (originalRow) => originalRow.applicantPhone,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "applicantAddress",
    header: "Address",
    accessor: (originalRow) => safeJoin([originalRow.applicantAddress.address1, originalRow.applicantAddress.address2]),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
  {
    id: "applicantCity",
    header: "City",
    accessor: (originalRow) => originalRow.applicantCity,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "applicantState",
    header: "State",
    accessor: (originalRow) => originalRow.applicantState,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "applicantPostalCode",
    header: "Postal Code",
    accessor: (originalRow) => originalRow.applicantPostalCode,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "connectionDate",
    header: "Connection Date",
    accessor: (originalRow) => formatDate(originalRow.connectionDate),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnMd,
  },
  {
    id: "applicantPricePoint",
    header: "Price Point",
    accessor: (originalRow) => originalRow.applicantPricePoint,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnSm,
  },
  {
    id: "applicantCategory",
    header: "Category",
    accessor: (originalRow) => {
      if (!Array.isArray(originalRow.applicantCategory)) return originalRow.applicantCategory;
      return originalRow.applicantCategory.length === 0 ? "-" : safeJoin(originalRow.applicantCategory);
    },
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: styles.tableColumnLg,
  },
];
